import { env } from '../config/env';
import { prefs } from '../db/prefs';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  // Absolute URL overrides env.baseUrl (used for the report endpoint).
  absoluteUrl?: string;
  auth?: boolean;
}

// Thin fetch wrapper that injects the Bearer device token (NetworkInterceptor.kt)
// and unwraps Vizzly's { code, message, data } envelope.
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, absoluteUrl, auth = true } = options;

  const url = absoluteUrl ?? env.baseUrl + path.replace(/^\//, '');

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = await prefs.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError((e as Error).message || 'Network error', 0);
  }

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const message =
      (json as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }

  return json as T;
}
